import {Evaluator, registerNode} from "@cm/graph-v2-final"
import {ImageOpNodeAffineTransform} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/affine-transform/node"
import {Matrix3x2} from "@cm/math"
import {ImageOpNodeGaussianBlur} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/composite/gaussian-blur/node"
import {ImageOpNodeMath} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/math/node"
import {ImageOpNodeBlendByLaplacianPyramid} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/blend-by-laplacian-pyramid/node"
import {ImageOpNodeBlend} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/blend/node"
import {definition} from "@app/textures/texture-editor/operator-stack/operators/layer-and-mask/node/definition"
import {
    AnyImageOpContext,
    AnyImageOpResultNode,
    ImageOpOutputs,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {CompositeImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/composite-image-op-node"

@registerNode
export class ImageOpNodeOperatorLayerAndMask extends CompositeImageOpNode<typeof definition> {
    static nodeClass = "ImageOpNodeOperatorLayerAndMask"
    static definition = definition

    override async execute(evaluator: Evaluator<AnyImageOpContext>, imageOpNodeBag: ImageOpNodeBag): Promise<ImageOpOutputs<typeof definition>> {
        const {
            sourceImage,
            mapOffsetInPixels,
            maskImage,
            maskOffsetInPixels,
            maskFeathering,
            drawMaskOnly,
            layerMinOpacity,
            layerMaxOpacity,
            useLaplacianPyramid,
        } = await evaluator.getAll(this.parameters)

        let shiftedMaskImage: AnyImageOpResultNode = imageOpNodeBag.getNode("shiftMask", ImageOpNodeAffineTransform, {
            sourceImage: maskImage,
            transform: new Matrix3x2().translate(maskOffsetInPixels),
            addressMode: "wrap",
        })
        if (maskFeathering > 0) {
            shiftedMaskImage = imageOpNodeBag.getNode("gaussianBlur", ImageOpNodeGaussianBlur, {
                sourceImage: shiftedMaskImage,
                sigma: (maskFeathering + 1) / 3,
                borderMode: "wrap",
            })
        }
        if (drawMaskOnly) {
            return await evaluator.get(shiftedMaskImage)
        } else {
            if (layerMinOpacity !== 0 || layerMaxOpacity !== 1) {
                const offset = layerMinOpacity
                const scale = layerMaxOpacity - layerMinOpacity
                if (scale !== 1) {
                    shiftedMaskImage = imageOpNodeBag.getNode("scale", ImageOpNodeMath, {
                        operandA: shiftedMaskImage,
                        operandB: scale,
                        operator: "*",
                    })
                }
                if (offset !== 0) {
                    shiftedMaskImage = imageOpNodeBag.getNode("offset", ImageOpNodeMath, {
                        operandA: shiftedMaskImage,
                        operandB: offset,
                        operator: "+",
                    })
                }
            }
            const shiftedSourceImage = imageOpNodeBag.getNode("shiftImage", ImageOpNodeAffineTransform, {
                sourceImage: sourceImage,
                transform: new Matrix3x2().translate(mapOffsetInPixels),
                addressMode: "wrap",
            })
            let resultImage: AnyImageOpResultNode
            if (useLaplacianPyramid) {
                resultImage = imageOpNodeBag.getNode("blendByLaplacianPyramid", ImageOpNodeBlendByLaplacianPyramid, {
                    backgroundImage: sourceImage,
                    foregroundImage: shiftedSourceImage,
                    maskImage: shiftedMaskImage,
                })
            } else {
                resultImage = imageOpNodeBag.getNode("blend", ImageOpNodeBlend, {
                    backgroundImage: sourceImage,
                    foregroundImage: shiftedSourceImage,
                    alpha: shiftedMaskImage,
                    premultipliedAlpha: false,
                    blendMode: "over",
                })
            }

            return await evaluator.get(resultImage)
        }
    }
}
